/**
 * +field: ground loot on field maps (docs/design/zones.md "Pickups"), on top of the world-resources
 * pickup machinery (worldloot/index.ts: arc, magnet, collect, 'pickup' events):
 *
 *   - kill drops: a gold pile now and then, rarely a gear drop (elites always drop one);
 *   - XP wisps: a ring of floating orbs at a random spot; no magnet, run through them; wisps taken
 *     in a row (within WISP_CHAIN_MS) pay up to +60%;
 *   - coin pouches: a lone gold pile somewhere on the map;
 *   - treasure chests: a 'cache' object (stand at it to open), loot rolled at spawn, despawns
 *     unopened after CHEST_LIFE_MS;
 *   - the treasure sprite: a fleeing mob (NpcDef.flees) that runs from the hero and escapes after
 *     SPRITE_LIFE_MS; killing it pays a big burst.
 *
 * Amounts scale with the map's level (the middle of its band) through `lootUnit` = an even-level
 * kill's XP, so pickups stay a fixed share of income (≈ 20%, zones.md). Everything uses ctx.rng
 * (deterministic per seed); only the looter (the hero) collects.
 *
 * +zone: on maps with areas, ambient loot lands within AMBIENT_NEAR of a hero (a big zone would
 * scatter it where nobody is) and is sized for the area's band; FieldDef.secrets place hidden
 * chests that come back `respawnMs` after they are opened.
 */
import type { FieldDef, FieldLootDef, FieldLootState, PickupRarity, PickupState, SimContext, Unit, Vec2, WorldObjectState, WorldResourceKind } from '../contracts';
import { fieldLevelAt } from './defs';

/** Defaults when a field sets `loot` without some field (and for tests). */
export const DEFAULT_FIELD_LOOT: FieldLootDef = {
  killGoldChance: 0.3,
  killItemChance: 0.025,
  wispEveryMs: [60_000, 90_000],
  pouchEveryMs: [60_000, 90_000],
  chestEveryMs: [150_000, 210_000],
  spriteEveryMs: [240_000, 360_000],
};

/** XP wisps per cluster, ring radius, life. */
export const WISP_COUNT = 5;
const WISP_RING = 3;
export const WISP_LIFE_MS = 75_000;
/** Each wisp pays this share of an even-level kill's XP; chained wisps add WISP_CHAIN_BONUS each (cap). */
export const WISP_XP_SHARE = 0.25;
export const WISP_CHAIN_MS = 2500;
export const WISP_CHAIN_BONUS = 0.12;
export const WISP_CHAIN_CAP = 0.6;
/** Wisps are taken by running through them (yd from the hero's centre). */
export const WISP_TOUCH = 1.4;
/** Ground drops (piles, items) vanish if left this long; at most this many wisp clusters live. */
export const DROP_LIFE_MS = 120_000;
const MAX_WISP_CLUSTERS = 2;
/** Chests and the sprite. */
export const CHEST_LIFE_MS = 240_000;
export const SPRITE_LIFE_MS = 40_000;
/** The sprite bolts once a hero is this close, to a spot this far away. */
const SPRITE_SCARE = 14;
const SPRITE_RUN = 12;
/** Ambient spawns keep this far from portals and heroes. */
const SPAWN_CLEAR = 9;
/** +zone: on maps with areas, ambient spawns land this far (max) from a hero. */
const AMBIENT_NEAR = 45;
/** +zone: a secret chest comes back this long after it is opened (default). */
export const SECRET_RESPAWN_MS = 600_000;
/** +zone: a secret chest pays this many kill-units of XP (a normal chest pays 2). */
const SECRET_XP_UNITS = 4;

/** An even-level kill's XP at `level` (the unit every loot amount scales with). */
export function lootUnit(level: number): number {
  return 10 + 4 * Math.max(1, level);
}

/** The level loot is sized for on a field: the middle of its band. */
export function fieldLootLevel(field: Pick<FieldDef, 'levelBand'>): number {
  return Math.round((field.levelBand[0] + field.levelBand[1]) / 2);
}

/** Gold of a kill's gold pile / a coin pouch / a chest / the sprite at `level`. */
export const lootGold = {
  pile: (level: number) => 2 + level,
  pouch: (level: number) => 8 + 3 * level,
  chest: (level: number) => 20 + 6 * level,
  sprite: (level: number) => 40 + 10 * level,
};

const roll = (ctx: SimContext, r: [number, number]): number => (r[1] > r[0] ? ctx.rng.int(r[0], r[1]) : r[0]);

export function initFieldLoot(ctx: SimContext, field: FieldDef): FieldLootState | undefined {
  const def = field.loot;
  if (!def) return undefined;
  const now = ctx.now;
  // The first wisps and pouch come soon so a fresh map has something shiny; chests and the sprite later.
  return {
    nextWispAt: now + Math.round(roll(ctx, def.wispEveryMs) * 0.25),
    nextPouchAt: now + Math.round(roll(ctx, def.pouchEveryMs) * 0.5),
    nextChestAt: now + Math.round(roll(ctx, def.chestEveryMs) * 0.5),
    nextSpriteAt: now + roll(ctx, def.spriteEveryMs),
    chain: { count: 0, lastAt: -1e9 },
    spriteId: null,
    spriteUntil: 0,
    seq: 0,
  };
}

/** Per tick (field/index.ts, after deaths and the world-loot tick). */
export function tickFieldLoot(ctx: SimContext, field: FieldDef): void {
  const w = ctx.world;
  const st = w.field?.loot;
  const def = field.loot;
  if (!st || !def || !w.worldLoot || !w.pickups || !w.worldObjects) return;
  const now = ctx.now;
  // +zone: secrets come back a while after they were opened.
  if (field.secrets?.length) tickSecrets(ctx, field, st);
  const zoned = !!field.areas?.length;
  const hero = zoned ? w.units[w.partyIds[0] ?? ''] : undefined;
  const level = zoned && hero ? lootLevelAt(field, hero.pos) : fieldLootLevel(field);

  // Kill drops (this tick's deaths; the corpse is still in the world).
  for (const e of w.events) {
    if (e.type !== 'death') continue;
    const u = w.units[e.targetID];
    if (!u || u.kind !== 'npc' || u.faction !== 'enemy') continue;
    if (u.id === st.spriteId) {
      spriteBurst(ctx, st, u, level);
      st.spriteId = null;
      continue;
    }
    if (!u.spawnId) continue;
    const lvl = u.level ?? (zoned ? lootLevelAt(field, u.pos) : level);
    if (ctx.rng.chance(def.killGoldChance)) drop(ctx, st, u.pos, 'gold', lootGold.pile(lvl), 'common');
    if (u.elite || ctx.rng.chance(def.killItemChance)) drop(ctx, st, u.pos, 'item', 1, u.elite ? 'rare' : itemRarity(ctx, 0));
  }

  // Expire stale drops and unopened chests.
  if (w.pickups.some((p) => p.expiresAt !== undefined && now >= p.expiresAt)) w.pickups = w.pickups.filter((p) => p.expiresAt === undefined || now < p.expiresAt);
  if (w.worldObjects.some((o) => o.expiresAt !== undefined && !o.broken && now >= o.expiresAt)) w.worldObjects = w.worldObjects.filter((o) => o.expiresAt === undefined || o.broken || now < o.expiresAt);

  // Ambient spawns.
  if (now >= st.nextWispAt) {
    st.nextWispAt = now + roll(ctx, def.wispEveryMs);
    const clusters = new Set(w.pickups.filter((p) => p.wisp).map((p) => p.objectId)).size;
    const at = clusters < MAX_WISP_CLUSTERS ? spawnSpot(ctx, field) : null;
    if (at) wispCluster(ctx, st, at, level);
  }
  if (now >= st.nextPouchAt) {
    st.nextPouchAt = now + roll(ctx, def.pouchEveryMs);
    const at = spawnSpot(ctx, field);
    if (at) drop(ctx, st, at, 'gold', lootGold.pouch(level), 'uncommon', true);
  }
  if (now >= st.nextChestAt) {
    st.nextChestAt = now + roll(ctx, def.chestEveryMs);
    const live = w.worldObjects.some((o) => o.kind === 'treasure_chest' && !o.broken && !o.id.startsWith('secret:'));
    const at = live ? null : spawnSpot(ctx, field);
    if (at) spawnChest(ctx, st, at, level);
  }
  if (now >= st.nextSpriteAt && st.spriteId === null && def.spriteNpcId && ctx.content.npc(def.spriteNpcId)) {
    st.nextSpriteAt = now + roll(ctx, def.spriteEveryMs);
    const at = spawnSpot(ctx, field);
    if (at) spawnSprite(ctx, st, def.spriteNpcId, at, level);
  }
  tickSprite(ctx, st);
}

/** Collecting an XP wisp: the chain bonus (called by worldloot when a wisp is collected). */
export function wispAmount(ctx: SimContext, p: PickupState): { amount: number; chain: number } {
  const st = ctx.world.field?.loot;
  if (!st) return { amount: p.amount, chain: 1 };
  st.chain.count = ctx.now - st.chain.lastAt <= WISP_CHAIN_MS ? st.chain.count + 1 : 1;
  st.chain.lastAt = ctx.now;
  const bonus = Math.min(WISP_CHAIN_CAP, WISP_CHAIN_BONUS * (st.chain.count - 1));
  return { amount: Math.round(p.amount * (1 + bonus)), chain: st.chain.count };
}

// ---------------------------------------------------------------------------

/** Gear rarity for a drop: common 70% / uncommon 25% / rare 5%, nudged up by `boost` (0..1). */
function itemRarity(ctx: SimContext, boost: number): PickupRarity {
  const r = ctx.rng.next() * (1 - boost);
  return r < 0.05 ? 'rare' : r < 0.3 ? 'uncommon' : 'common';
}

function nextId(st: FieldLootState, prefix: string): string {
  st.seq += 1;
  return `${prefix}${st.seq}`;
}

/** One pickup that arcs out from `from` onto walkable floor nearby. */
function drop(ctx: SimContext, st: FieldLootState, from: Vec2, resource: WorldResourceKind, amount: number, rarity: PickupRarity, still = false): void {
  const w = ctx.world;
  const ang = ctx.rng.range(0, Math.PI * 2);
  const r = still ? 0 : ctx.rng.range(0.8, 2);
  const pos = ctx.sys.spatial.clampToWalkable(w, { x: from.x + Math.cos(ang) * r, y: from.y + Math.sin(ang) * r });
  const id = nextId(st, 'fl');
  const p: PickupState = {
    id,
    resource,
    amount: Math.max(1, Math.round(amount)),
    rarity,
    objectId: id,
    from: { ...from },
    pos: { x: Math.round(pos.x * 100) / 100, y: Math.round(pos.y * 100) / 100 },
    spawnedAt: ctx.now,
    landAt: ctx.now + (still ? 0 : 520),
    magnetSince: null,
    expiresAt: ctx.now + DROP_LIFE_MS,
  };
  w.pickups!.push(p);
  ctx.emit({ type: 'pickup_spawn', pickupId: p.id, objectId: p.objectId, resource, amount: p.amount, rarity, from: p.from, pos: p.pos, landAt: p.landAt });
}

function wispCluster(ctx: SimContext, st: FieldLootState, at: Vec2, level: number): void {
  const w = ctx.world;
  const cluster = nextId(st, 'wc');
  const amount = Math.max(1, Math.round(lootUnit(level) * WISP_XP_SHARE));
  const a0 = ctx.rng.range(0, Math.PI * 2);
  for (let i = 0; i < WISP_COUNT; i++) {
    const a = a0 + (i / WISP_COUNT) * Math.PI * 2;
    const pos = ctx.sys.spatial.clampToWalkable(w, { x: at.x + Math.cos(a) * WISP_RING, y: at.y + Math.sin(a) * WISP_RING });
    const id = `${cluster}.${i}`;
    // Wisps glow violet (the 'epic' glow + a short beam) so they read as magic, not loot.
    const p: PickupState = { id, resource: 'xp', amount, rarity: 'epic', objectId: cluster, from: { ...pos }, pos: { x: Math.round(pos.x * 100) / 100, y: Math.round(pos.y * 100) / 100 }, spawnedAt: ctx.now, landAt: ctx.now, magnetSince: null, wisp: true, expiresAt: ctx.now + WISP_LIFE_MS };
    w.pickups!.push(p);
    ctx.emit({ type: 'pickup_spawn', pickupId: id, objectId: cluster, resource: 'xp', amount, rarity: 'epic', from: p.from, pos: p.pos, landAt: p.landAt, wisp: true });
  }
}

function spawnChest(ctx: SimContext, st: FieldLootState, at: Vec2, level: number): void {
  const loot: NonNullable<WorldObjectState['loot']> = [
    { resource: 'gold', amount: lootGold.chest(level) },
    { resource: 'xp', amount: Math.round(lootUnit(level) * 2) },
    { resource: 'item', amount: 1, rarity: ctx.rng.chance(0.3) ? 'rare' : 'uncommon' },
  ];
  ctx.world.worldObjects!.push({
    id: nextId(st, 'chest'),
    kind: 'treasure_chest',
    class: 'cache',
    pos: { x: Math.round(at.x * 100) / 100, y: Math.round(at.y * 100) / 100 },
    facing: ctx.rng.range(-Math.PI, Math.PI),
    roomId: '',
    radius: 0.9,
    hp: 1,
    maxHp: 1,
    hidden: false,
    discovered: true,
    broken: false,
    brokenAt: null,
    lastHitAt: null,
    openProgressMs: 0,
    variant: 0,
    loot,
    expiresAt: ctx.now + CHEST_LIFE_MS,
  });
}

function spawnSprite(ctx: SimContext, st: FieldLootState, npcId: string, at: Vec2, level: number): void {
  const u = ctx.ops.spawnNpc({ npcId, pos: at, facing: ctx.rng.range(-Math.PI, Math.PI), packId: null });
  u.level = level;
  st.spriteId = u.id;
  st.spriteUntil = ctx.now + SPRITE_LIFE_MS;
  ctx.emit({ type: 'field_spawn', sourceID: u.id, spawnId: 'treasure_sprite', level });
}

/** The sprite runs from the nearest hero; it escapes (vanishes) when its time is up. */
function tickSprite(ctx: SimContext, st: FieldLootState): void {
  const w = ctx.world;
  if (st.spriteId === null) return;
  const s = w.units[st.spriteId];
  if (!s || !s.alive) {
    st.spriteId = null;
    return;
  }
  if (ctx.now >= st.spriteUntil) {
    delete w.units[s.id];
    w.unitOrder = w.unitOrder.filter((id) => id !== s.id);
    for (const id of w.partyIds) if (w.units[id]?.targetId === s.id) w.units[id]!.targetId = null;
    st.spriteId = null;
    return;
  }
  // Never leashes: home follows it.
  s.home = { x: s.pos.x, y: s.pos.y };
  if (s.move && s.move.mode !== 'dir') return;
  let near: Unit | null = null;
  let best = SPRITE_SCARE;
  for (const id of w.partyIds) {
    const h = w.units[id];
    if (!h?.alive) continue;
    const d = Math.hypot(h.pos.x - s.pos.x, h.pos.y - s.pos.y);
    if (d < best) {
      best = d;
      near = h;
    }
  }
  if (!near) return;
  ctx.sys.spatial.setDestination(ctx, s.id, fleePoint(ctx, s, near.pos));
}

/** A walkable point SPRITE_RUN yd away from `from` (tries a fan of directions, seeded). */
export function fleePoint(ctx: SimContext, u: Unit, from: Vec2): Vec2 {
  const base = Math.atan2(u.pos.y - from.y, u.pos.x - from.x);
  const jitter = ctx.rng.range(-0.5, 0.5);
  for (const off of [0, 0.7, -0.7, 1.4, -1.4, 2.2, -2.2]) {
    const a = base + off + jitter;
    const p = { x: u.pos.x + Math.cos(a) * SPRITE_RUN, y: u.pos.y + Math.sin(a) * SPRITE_RUN };
    if (ctx.sys.spatial.isWalkable(ctx.world, p)) return p;
  }
  return ctx.sys.spatial.clampToWalkable(ctx.world, { x: u.pos.x - Math.cos(base) * SPRITE_RUN, y: u.pos.y - Math.sin(base) * SPRITE_RUN });
}

function pointIn(p: Vec2, pts: readonly Vec2[]): boolean {
  let inside = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const a = pts[i]!;
    const b = pts[j]!;
    if (a.y > p.y !== b.y > p.y && p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x) inside = !inside;
  }
  return inside;
}

function spriteBurst(ctx: SimContext, st: FieldLootState, u: Unit, level: number): void {
  const gold = lootGold.sprite(level);
  for (let i = 0; i < 4; i++) drop(ctx, st, u.pos, 'gold', Math.round(gold / 4), 'uncommon');
  drop(ctx, st, u.pos, 'xp', Math.round(lootUnit(level) * 5), 'rare');
  drop(ctx, st, u.pos, 'item', 1, ctx.rng.chance(0.2) ? 'epic' : 'rare');
}

/** +zone: loot level at a spot (the middle of its area's band). */
function lootLevelAt(field: FieldDef, p: Vec2): number {
  const b = fieldLevelAt(field, p);
  return Math.round((b[0] + b[1]) / 2);
}

function spawnSecret(ctx: SimContext, field: FieldDef, sec: NonNullable<FieldDef['secrets']>[number]): void {
  const level = sec.level ?? lootLevelAt(field, sec.pos);
  // +glide: a sky-puzzle chest pays a little more (half again the gold and XP, a better shot at an epic).
  const sky = sec.sky === true;
  const loot: NonNullable<WorldObjectState['loot']> = [
    { resource: 'gold', amount: lootGold.chest(level) * (sky ? 3 : 2) },
    { resource: 'xp', amount: Math.round(lootUnit(level) * SECRET_XP_UNITS * (sky ? 1.5 : 1)) },
    { resource: 'item', amount: 1, rarity: ctx.rng.chance(sky ? 0.6 : 0.4) ? 'epic' : 'rare' },
  ];
  ctx.world.worldObjects?.push({
    id: `secret:${sec.id}`,
    kind: 'treasure_chest',
    class: 'cache',
    pos: { x: sec.pos.x, y: sec.pos.y },
    facing: 0,
    roomId: '',
    radius: 0.9,
    hp: 1,
    maxHp: 1,
    hidden: false,
    discovered: true,
    broken: false,
    brokenAt: null,
    lastHitAt: null,
    openProgressMs: 0,
    variant: 1,
    loot,
    ...(sec.z !== undefined ? { z: sec.z } : {}), // +glide
  });
}

/** Opened secrets leave the world and come back after their respawn time. */
function tickSecrets(ctx: SimContext, field: FieldDef, st: FieldLootState): void {
  const w = ctx.world;
  const at = (st.secretsAt ??= {});
  for (const sec of field.secrets ?? []) {
    const oid = `secret:${sec.id}`;
    const o = w.worldObjects!.find((x) => x.id === oid);
    // Placed on the first tick (the world-objects list is set up after the field).
    if (!o && at[sec.id] === undefined) {
      spawnSecret(ctx, field, sec);
      continue;
    }
    if (o?.broken && at[sec.id] === undefined) at[sec.id] = ctx.now + (sec.respawnMs ?? SECRET_RESPAWN_MS);
    if (at[sec.id] !== undefined && ctx.now >= at[sec.id]!) {
      delete at[sec.id];
      w.worldObjects = w.worldObjects!.filter((x) => x.id !== oid);
      spawnSecret(ctx, field, sec);
    }
  }
}

/** A random walkable spot clear of portals and heroes (null after a few misses); near a hero on zoned maps. */
function spawnSpot(ctx: SimContext, field: FieldDef): Vec2 | null {
  const w = ctx.world;
  const d = ctx.content.dungeon(w.dungeonId);
  if (!d) return null;
  const b = d.bounds;
  const near = field.areas?.length ? w.units[w.partyIds[0] ?? ''] : undefined;
  for (let i = 0; i < 12; i++) {
    let p: Vec2;
    if (near) {
      const a = ctx.rng.range(0, Math.PI * 2);
      const r = ctx.rng.range(SPAWN_CLEAR + 4, AMBIENT_NEAR);
      p = { x: near.pos.x + Math.cos(a) * r, y: near.pos.y + Math.sin(a) * r };
    } else p = { x: ctx.rng.range(b.x, b.x + b.w), y: ctx.rng.range(b.y, b.y + b.h) };
    if (near && field.areas?.length) {
      // Not in a safe area (town).
      const ar = field.areas.find((x) => x.safe && pointIn(p, x.shape.points));
      if (ar) continue;
    }
    if (!ctx.sys.spatial.isWalkable(w, p)) continue;
    if (field.portals.some((q) => Math.hypot(q.pos.x - p.x, q.pos.y - p.y) < q.radius + SPAWN_CLEAR)) continue;
    if (w.partyIds.some((id) => {
      const h = w.units[id];
      return !!h && Math.hypot(h.pos.x - p.x, h.pos.y - p.y) < SPAWN_CLEAR;
    }))
      continue;
    return p;
  }
  return null;
}
